import React from 'react';
import { useTutorialOpcional } from '../tutorial/TutorialContext';
import {
  PLAYBACK_RATES,
  formatearVelocidad,
  type PlaybackRate,
} from '../../services/tutorial/playbackRate';

interface GuideControlsProps {
  onClose: () => void;
  onComplete: () => void;
  disabled?: boolean;
  stepText?: string;
  currentStep?: number;
  totalSteps?: number;
  /** Origen de la guía: 'golden' (oficial) | 'llm' (IA) | undefined */
  source?: 'golden' | 'llm';
  /** Audio narrado del paso (tutorial de onboarding). Opcional. */
  audioUrl?: string;
  /** Paso SIN nada que clickear (sólo texto): el botón dice "Siguiente"
   *  en vez de "Ya lo hice" — no hay acción hecha que confirmar. */
  pasoInformativo?: boolean;
}

/** Preferencia de mute del narrador, persistida (global del browser). */
const MUTE_KEY = 'mcd:tutorial:audio-muted';
const isMuted = () => {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
};

export const GuideControls: React.FC<GuideControlsProps> = ({
  onClose,
  onComplete,
  disabled = false,
  stepText,
  currentStep = 1,
  totalSteps = 1,
  source,
  audioUrl,
  pasoInformativo = false,
}) => {
  // ---- Narración de voz del paso (si existe el archivo) ----
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = React.useState(false);
  const [audioError, setAudioError] = React.useState(false);
  const [muted, setMuted] = React.useState(isMuted);

  // ---- Velocidad de la narración (sólo tutorial de onboarding) ----
  // El estado vive en TutorialContext (persistido por usuario); esta barra es
  // genérica (también sirve guías del chat), así que se toma por hook
  // opcional y el selector sólo aparece con un módulo del tutorial activo.
  const tutorial = useTutorialOpcional();
  const playbackRate: PlaybackRate = tutorial?.playbackRate ?? 1;
  const esTutorial = Boolean(tutorial?.activeModuleId);

  // ---- Colapso de la barra ----
  // Manual (botón –) o automático mientras haya un modal abierto: la barra
  // no debe tapar lo que explica (en mobile los modales son bottom-sheets y
  // la barra fija abajo les cae justo encima). Vigilancia por rol ARIA: los
  // Modal/ConfirmationModal de la app se desmontan al cerrar, así que la
  // pastilla desaparece y la barra vuelve sola — salvo colapso manual, que
  // es decisión del usuario y se mantiene.
  const [colapsadoManual, setColapsadoManual] = React.useState(false);
  const [modalAbierto, setModalAbierto] = React.useState(false);
  React.useEffect(() => {
    const revisar = () =>
      setModalAbierto(document.querySelector('[role="dialog"]') !== null);
    revisar();
    const interval = setInterval(revisar, 300);
    return () => clearInterval(interval);
  }, []);
  const colapsado = colapsadoManual || modalAbierto;

  // Velocidad de la narración: aplicar sin cortar la reproducción (cambiás el
  // chip y el audio en curso sigue al nuevo ritmo). Se re-aplica al cambiar de
  // paso porque el src nuevo resetea playbackRate a 1: defaultPlaybackRate
  // cubre el reset asíncrono del elemento, playbackRate el cambio en vivo.
  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.defaultPlaybackRate = playbackRate;
    audio.playbackRate = playbackRate;
  }, [playbackRate, audioUrl]);

  // Al cambiar de paso: corta la narración anterior e intenta autoreproducir
  // la nueva (si no está muteado). Si el browser bloquea el autoplay o el
  // archivo no existe (404), queda el botón manual.
  React.useEffect(() => {
    setPlaying(false);
    setAudioError(false);
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    if (audioUrl && !muted) {
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }, [audioUrl, muted]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || audioError) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    try {
      localStorage.setItem(MUTE_KEY, next ? '1' : '0');
    } catch {
      /* noop */
    }
    const audio = audioRef.current;
    if (next && audio) {
      audio.pause();
      setPlaying(false);
    }
  };

  const muestraNarrador = Boolean(audioUrl) && !audioError;

  // Badge sutil indicando el origen de la guía.
  // Si source es undefined (guías antiguas / edge cases) no se renderiza nada.
  const sourceBadge = source === 'golden' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-success/10 text-success text-xs font-medium border border-success/30">
      Guía oficial
    </span>
  ) : source === 'llm' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-paper-2 text-sage text-xs font-medium border border-hairline">
      Generada por IA
    </span>
  ) : null;

  return (
    <>
      {/* El <audio> vive FUERA de las dos vistas: colapsar la barra no debe
          cortar la narración del paso que está sonando. */}
      {muestraNarrador && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onError={() => setAudioError(true)}
          onEnded={() => setPlaying(false)}
          preload="auto"
        />
      )}

      {colapsado ? (
        /* Pastilla colapsada: paso + narración + avance, sin cuerpo de texto.
           Con un modal abierto vive ARRIBA (en mobile los modales nacen del
           borde inferior); colapsada a mano queda abajo, donde vivía la
           barra. Tocar el cuerpo de la pastilla la vuelve a expandir. */
        <div className={`fixed left-1/2 -translate-x-1/2 z-[60] ${modalAbierto ? 'top-[68px]' : 'bottom-4'}`}>
          <div className="flex items-center gap-1 rounded border-2 border-trigo bg-paper px-2 py-1 shadow-overlay">
            <button
              onClick={() => setColapsadoManual(false)}
              className="pointer-events-auto flex items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold text-ink transition-colors hover:bg-paper-2"
              aria-label={`Expandir tutorial (paso ${currentStep} de ${totalSteps})`}
              title="Expandir panel del tutorial"
            >
              <span aria-hidden="true">🎯</span> Paso {currentStep}/{totalSteps}
            </button>
            {muestraNarrador && (
              <button
                onClick={togglePlay}
                className="pointer-events-auto flex h-6 w-6 items-center justify-center rounded-pixel border border-hairline bg-paper text-[10px] transition-colors hover:bg-paper-2"
                aria-label={playing ? 'Pausar narración' : 'Escuchar narración'}
                title={playing ? 'Pausar narración' : 'Escuchar narración'}
              >
                <span aria-hidden="true">{playing ? '⏸️' : '▶️'}</span>
              </button>
            )}
            {!disabled && (
              <button
                onClick={onComplete}
                className="pointer-events-auto flex h-6 w-6 items-center justify-center rounded-pixel bg-forest text-xs font-bold text-paper transition-colors hover:bg-forest-mid"
                aria-label="Ya lo hice: avanzar al siguiente paso"
                title="Ya lo hice"
              >
                ✓
              </button>
            )}
          </div>
        </div>
      ) : (
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-[60] pointer-events-none">
        <div className="bg-paper rounded-lg shadow-overlay border-2 border-trigo p-4 max-w-md mx-4">
          {/* Header del tutorial */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-trigo rounded-pixel flex items-center justify-center">
                <span className="text-paper text-sm font-bold">🎯</span>
              </div>
              <div>
                <h3 className="text-sm font-display font-bold text-ink">Tutorial Activo</h3>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-sage">Paso {currentStep} de {totalSteps}</p>
                  {sourceBadge}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setColapsadoManual(true)}
                className="pointer-events-auto w-8 h-8 bg-paper-2 hover:bg-hairline/60 rounded-pixel flex items-center justify-center transition-colors"
                aria-label="Minimizar tutorial"
                title="Minimizar: la barra se achica a una pastilla"
              >
                <span className="text-sage text-lg leading-none">–</span>
              </button>
              <button
                onClick={onClose}
                className="pointer-events-auto w-8 h-8 bg-paper-2 hover:bg-hairline/60 rounded-pixel flex items-center justify-center transition-colors"
                aria-label="Cerrar tutorial"
              >
                <span className="text-sage text-lg">×</span>
              </button>
            </div>
          </div>

          {/* Texto del paso actual */}
          {stepText && (
            <div className="bg-paper-2/60 rounded-lg p-3 mb-3 border border-hairline">
              <p className="text-sm text-ink leading-relaxed">{stepText}</p>
              {/* Narrador de voz del paso (tutorial): play/pausa + mute */}
              {muestraNarrador && (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={togglePlay}
                    className="pointer-events-auto inline-flex items-center gap-1.5 rounded border border-hairline bg-paper px-2.5 py-1 text-xs font-medium text-ink transition-colors hover:bg-paper-2"
                    aria-label={playing ? 'Pausar narración' : 'Escuchar narración'}
                    title={playing ? 'Pausar narración' : 'Escuchar narración'}
                  >
                    <span aria-hidden="true">{playing ? '⏸️' : '▶️'}</span>
                    {playing ? 'Pausar' : 'Escuchar'}
                  </button>
                  <button
                    onClick={toggleMute}
                    className="pointer-events-auto inline-flex items-center justify-center rounded border border-hairline bg-paper px-2 py-1 text-xs text-sage transition-colors hover:bg-paper-2"
                    aria-label={muted ? 'Activar narrador' : 'Silenciar narrador'}
                    title={muted ? 'Activar narrador' : 'Silenciar narrador (no se reproduce solo)'}
                  >
                    {muted ? '🔇' : '🔊'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Velocidad de la narración (tutorial): aplica al audio en curso
              y a los próximos pasos; visible aunque este paso no tenga audio. */}
          {esTutorial && (
            <div
              className="mb-3 flex items-center justify-center gap-1"
              role="group"
              aria-label="Velocidad de la narración"
            >
              <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-sage">
                Velocidad
              </span>
              {PLAYBACK_RATES.map(rate => (
                <button
                  key={rate}
                  type="button"
                  onClick={() => tutorial?.setPlaybackRate(rate)}
                  aria-pressed={playbackRate === rate}
                  className={`pointer-events-auto rounded border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                    playbackRate === rate
                      ? 'border-trigo bg-trigo text-paper'
                      : 'border-hairline bg-paper text-sage hover:bg-paper-2'
                  }`}
                >
                  {formatearVelocidad(rate)}
                </button>
              ))}
            </div>
          )}

          {/* Botón de acción: "Ya lo hice" cuando el paso señala algo que
              hacer, "Siguiente" cuando es sólo informativo. */}
          {!disabled && (
            <button
              onClick={onComplete}
              className="pointer-events-auto w-full bg-forest text-paper font-semibold py-3 px-4 rounded-lg hover:bg-forest-mid transition-colors shadow-card flex items-center justify-center gap-2"
            >
              {pasoInformativo ? (
                <>
                  <span>Siguiente</span>
                  <span className="text-xs opacity-75">→</span>
                </>
              ) : (
                <>
                  <span className="text-lg">✓</span>
                  <span>Ya lo hice</span>
                  <span className="text-xs opacity-75">→</span>
                </>
              )}
            </button>
          )}

          {/* Progress bar */}
          <div className="mt-3 bg-hairline/50 rounded-sm h-2 overflow-hidden">
            <div
              className="bg-trigo h-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </div>
      )}
    </>
  );
};
